import React from "react";
import { useNavigate, useLocation } from "react-router";
import orderBy from "lodash/orderBy";
import TextField from "@mui/material/TextField";
import { useQuery } from "@apollo/client/react";
import CircularProgress from "@mui/material/CircularProgress";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Tooltip from "@mui/material/Tooltip";
import { ASSET_INVENTORY_ITEMS_QUERY } from "../constants/graphql/queries";
import useLabels from "../hooks/useLabels";
import useQueryParams from "../hooks/useQueryParams";
import OutboundSummaryModal from "./OutboundSummaryModal";

const formatOutboundSummary = (inventoryItems = []) => {
  return orderBy(
    inventoryItems.filter(item => item.inventoryRequest),
    v => v.inventoryRequest.workOrderNumber,
    "asc"
  )
    .map(item => `${item.inventoryRequest.workOrderNumber} (${item.quantity})`)
    .join("\n");
};

export default function OutboundSummary({ asset, disabled }) {
  const l = useLabels();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useQueryParams();
  // Use inventoryItems from the parent prop when available (e.g. Assets table page),
  // otherwise fetch them directly (e.g. capture-inventory page)
  const parentInventoryItems = asset?.inventoryItems;
  const needsFetch = !parentInventoryItems;
  const { data, loading } = useQuery(ASSET_INVENTORY_ITEMS_QUERY, {
    variables: { id: asset.id },
    skip: !needsFetch || !asset.id,
  });
  const inventoryItems = parentInventoryItems ?? data?.asset?.inventoryItems ?? [];
  const hasItems = inventoryItems.length > 0;
  const isEditable = hasItems && !disabled;
  const currentAssetId = asset?.id ? String(asset.id) : null;
  const modalAssetId = params.get("outboundSummaryAssetId");
  const modalOpen = Boolean(currentAssetId && modalAssetId === currentAssetId);

  const updateModalRoute = isOpen => {
    if (!currentAssetId) {
      return;
    }

    const nextParams = new URLSearchParams(location.search);

    if (isOpen) {
      nextParams.set("outboundSummaryAssetId", currentAssetId);
    } else {
      nextParams.delete("outboundSummaryAssetId");
    }

    navigate({ pathname: location.pathname, search: nextParams.toString() });
  };

  const handleClick = () => {
    if (isEditable) {
      updateModalRoute(true);
    }
  };

  const handleClose = () => {
    updateModalRoute(false);
  };

  return (
    <>
      <TextField
        multiline
        size="small"
        label={l.identifiers.outbound_summary}
        value={loading ? "" : formatOutboundSummary(inventoryItems)}
        fullWidth
        disabled={disabled}
        InputProps={{
          endAdornment: loading ? (
            <CircularProgress size={20} />
          ) : isEditable ? (
            <Tooltip title="Click to view details">
              <InfoOutlinedIcon color="primary" fontSize="small" style={{ cursor: "pointer" }} />
            </Tooltip>
          ) : null,
          style: { cursor: isEditable ? "pointer" : "default" },
        }}
        onClick={handleClick}
      />
      {modalOpen && <OutboundSummaryModal asset={asset} onClose={handleClose} />}
    </>
  );
}
